/**
 * posNumpad.js
 *
 * Pure key handling for the POS numeric keypad (cart quantity, cash received).
 * Keeping it free of React makes the editing rules unit-testable.
 */

/** Keypad key identifiers besides the digits '0'..'9'. */
export const NUMPAD_KEYS = Object.freeze({BACK: 'back', CLEAR: 'clear', DOT: '.'});

/**
 * Applies one key press to the current draft string.
 *
 * @param {string|number|null|undefined} value Current draft.
 * @param {string} key '0'..'9', '.', 'back' or 'clear'.
 * @param {object} [options]
 * @param {number} [options.maxLength=9] Max characters of the draft.
 * @param {boolean} [options.allowDecimal=false] Whether '.' is accepted (one only).
 * @returns {string} New draft.
 */
export const applyNumpadKey = (value, key, {maxLength = 9, allowDecimal = false} = {}) => {
    const current = value === null || value === undefined ? '' : String(value);

    if (key === NUMPAD_KEYS.BACK) {
        return current.slice(0, -1);
    }

    if (key === NUMPAD_KEYS.CLEAR) {
        return '';
    }

    if (key === NUMPAD_KEYS.DOT || key === ',') {
        if (!allowDecimal || current.includes('.') || current.length >= maxLength) {
            return current;
        }

        return `${current === '' ? '0' : current}.`;
    }

    if (!/^\d$/.test(String(key))) {
        return current;
    }

    if (current === '0') {
        // No leading zeros ("0" then "5" is "5"), but a lone "0" stays a valid draft.
        return key === '0' ? current : key;
    }

    return current.length >= maxLength ? current : `${current}${key}`;
};

/**
 * Parses a draft into a number.
 *
 * @param {string} draft Draft string ("12", "12.5", "").
 * @returns {number} The number, or NaN for an empty / invalid draft.
 */
export const parseNumpadValue = (draft) => {
    if (draft === '' || draft === null || draft === undefined || draft === '.') {
        return NaN;
    }

    return Number(String(draft).replace(/\.$/, ''));
};
